import React, { useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  closestCorners,
  useSensor,
  useSensors,
  useDroppable,
} from "@dnd-kit/core";
import { SortableContext, useSortable, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import TaskCard, { SubtaskCard, GhostParentCard } from "./TaskCard";
import { getStatus } from "./taskConstants";
import {
  computeDropTarget,
  columnOnlyTarget,
  COLUMN_PREFIX,
  nestColumns,
  subtaskMovesWith,
} from "./taskBoardUtils";

const Line = () => <div className="h-0.5 rounded-full bg-primary" data-testid="drop-indicator" />;

// Kartlar yerinde durur; hedef yalnız çizgiyle gösterilir. İç içe (kart içi alt iş)
// düzende dnd-kit'in kaydırma animasyonu kartları yanlış yere iterdi.
const noShift = () => null;

// Bırakma çizgisinin hangi kartın üstüne/altına çizileceği.
const indicatorFor = (here, tasks) => (id) => {
  if (!here) return null;
  if (here.beforeId === id) return "bottom";
  if (!here.beforeId && here.afterId === id) return "top";
  // Komşusuz hedef (tüm projeler panosu) → sütun sonu.
  if (!here.beforeId && !here.afterId && tasks[tasks.length - 1]?._id === id) return "bottom";
  return null;
};

// Üst kartın sürükleme dinleyicileri iç kartta tetiklenmesin.
const stopFor = (listeners = {}) =>
  Object.fromEntries(
    Object.entries(listeners).map(([k, fn]) => [
      k,
      (e) => {
        e.stopPropagation();
        fn(e);
      },
    ])
  );

function SortableSubtask({ task, onSelect, indicator }) {
  const dragDisabled = task.can?.edit === false;
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({ id: task._id, disabled: dragDisabled });
  return (
    <div className="flex flex-col gap-1">
      {indicator === "top" && <Line />}
      <SubtaskCard
        ref={setNodeRef}
        task={task}
        onClick={onSelect}
        dragDisabled={dragDisabled}
        className={isDragging ? "opacity-40" : ""}
        {...attributes}
        {...stopFor(listeners)}
      />
      {indicator === "bottom" && <Line />}
    </div>
  );
}

function SortableCard({ task, onSelect, indicator, subtasks = [], indicatorOf }) {
  const dragDisabled = task.can?.edit === false;
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({ id: task._id, disabled: dragDisabled });
  return (
    <div className="flex flex-col gap-1">
      {indicator === "top" && <Line />}
      <TaskCard
        ref={setNodeRef}
        task={task}
        onClick={onSelect}
        dragDisabled={dragDisabled}
        className={isDragging ? "opacity-40" : ""}
        {...attributes}
        {...listeners}
        aria-disabled={dragDisabled ? "true" : undefined}
      >
        {subtasks.map((s) => (
          <SortableSubtask key={s._id} task={s} onSelect={onSelect} indicator={indicatorOf(s._id)} />
        ))}
      </TaskCard>
      {indicator === "bottom" && <Line />}
    </div>
  );
}

function Column({ column, onSelect, target }) {
  const { setNodeRef, isOver } = useDroppable({ id: `${COLUMN_PREFIX}${column.status}` });
  const { tasks, top, nested } = column;
  const here = target && target.status === column.status ? target : null;
  const ind = indicatorFor(here, tasks);
  return (
    <div className="flex flex-col w-72 shrink-0 bg-gray-50 rounded-2xl max-h-full" data-testid={`column-${column.status}`}>
      <div className="px-3 py-2.5 text-sm font-SemiBold text-gray-700 flex items-center justify-between">
        <span>{getStatus(column.status)?.label || column.status}</span>
        <span className="text-xs text-gray-500 font-Medium">{tasks.length}</span>
      </div>
      <div
        ref={setNodeRef}
        className={`flex flex-col gap-2 px-2 pb-2 overflow-y-auto min-h-[3rem] flex-1 rounded-b-2xl ${isOver ? "bg-gray-100" : ""}`}
      >
        <SortableContext items={tasks.map((t) => t._id)} strategy={noShift}>
          {top.map((t) =>
            t.ghost ? (
              <GhostParentCard
                key={t._id}
                parent={t.parent}
                project={t.project}
                statusLabel={t.parentStatus ? getStatus(t.parentStatus)?.label || t.parentStatus : null}
                onClick={onSelect}
              >
                {(nested.get(t.parent._id) || []).map((s) => (
                  <SortableSubtask key={s._id} task={s} onSelect={onSelect} indicator={ind(s._id)} />
                ))}
              </GhostParentCard>
            ) : (
              <SortableCard
                key={t._id}
                task={t}
                onSelect={onSelect}
                indicator={ind(t._id)}
                subtasks={nested.get(t._id) || []}
                indicatorOf={ind}
              />
            )
          )}
        </SortableContext>
        {here && tasks.length === 0 && <Line />}
        {tasks.length === 0 && <div className="text-xs text-gray-400 text-center py-4">Kart yok</div>}
      </div>
    </div>
  );
}

// Sürüklenen kartın merkezi hedef kartın merkezinin altındaysa kart hedefin altına eklenir.
const isBelow = (active, over) => {
  const a = active?.rect?.current?.translated;
  const o = over?.rect;
  if (!a || !o) return false;
  return a.top + a.height / 2 > o.top + o.height / 2;
};

// reorderable=false (tüm projeler panosu): sütun içi sıra sunucuda otomatik; kart
// yalnız başka sütuna taşınabilir ve komşusuz gönderilir (sütun sonuna gider).
// Alt işler (Client#127): üst kartı aynı sütundaysa onun gövdesinde, değilse o sütunda
// üst işin gölge kartının gövdesinde.
function TaskBoard({ columns = [], showCancelled = false, reorderable = true, onMove, onSelect }) {
  const [activeTask, setActiveTask] = useState(null);
  const [target, setTarget] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const visible = useMemo(
    () => nestColumns(columns.filter((c) => showCancelled || c.status !== "cancelled")),
    [columns, showCancelled]
  );

  const findTask = (id) => {
    for (const c of columns) {
      const t = (c.tasks || []).find((x) => x._id === id);
      if (t) return t;
    }
    return null;
  };

  const calcTarget = ({ active, over }) => {
    if (!over) return null;
    const drop = computeDropTarget(visible, active.id, over.id, { placeBelow: isBelow(active, over) });
    return reorderable ? drop : columnOnlyTarget(visible, active.id, drop);
  };

  const updateTarget = (event) => {
    const next = calcTarget(event);
    setTarget((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
  };

  const handleDragEnd = (event) => {
    const drop = calcTarget(event);
    setActiveTask(null);
    setTarget(null);
    if (!drop || !onMove) return;
    const task = findTask(event.active.id);
    if (!task) return;
    // Üst iş başka sütuna giderse kartındaki alt işler de onunla gider.
    const subtasks = subtaskMovesWith(columns, task._id, drop);
    onMove({ id: task._id, version: task.version, ...drop, subtasks });
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={({ active }) => setActiveTask(findTask(active.id))}
      onDragOver={updateTarget}
      onDragMove={updateTarget}
      onDragEnd={handleDragEnd}
      onDragCancel={() => {
        setActiveTask(null);
        setTarget(null);
      }}
    >
      <div className="flex gap-3 overflow-x-auto p-3 sm:p-4 md:p-6 h-full items-start">
        {visible.map((col) => (
          <Column key={col.status} column={col} onSelect={onSelect} target={target} />
        ))}
      </div>
      <DragOverlay>
        {activeTask ? (
          activeTask.parent ? (
            <SubtaskCard task={activeTask} className="shadow-lg w-64" showParent />
          ) : (
            // Üst iş kartındaki (aynı sütundaki) alt işlerle birlikte sürüklenir.
            <TaskCard task={activeTask} className="shadow-lg">
              {columns
                .find((c) => (c.tasks || []).some((t) => t._id === activeTask._id))
                ?.tasks?.filter((t) => t.parent?._id === activeTask._id)
                .map((s) => (
                  <SubtaskCard key={s._id} task={s} />
                ))}
            </TaskCard>
          )
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

export default TaskBoard;
